/* ===========================================================================
   Universal Security Instruments — Generated Product Spec Sheet (Letter, 8.5x11)
   ---------------------------------------------------------------------------
   Theme asset: assets/usi-specsheet-pdf.js

   WHAT THIS DOES
     Turns the live spec-sheet section (sections/spec-sheet.liquid +
     snippets/specifications-table.liquid) into a vector PDF on click.
     Spec groups/rows are harvested from the RENDERED DOM (.grid-container /
     .titleSpec / plain .item children, paired by POSITION — odd = label,
     even = value; the live page has no .itemLabel/.itemValue class split),
     so the PDF automatically follows every rule the Liquid already applies —
     which metafields are populated, how they're labeled, and which rows get
     dropped when blank. Update a metafield in Shopify and the next download
     reflects it; there is no static per-SKU PDF to re-export and re-upload.

     Product title, SKU, hero image, logo, and bullet features are read the
     same way, straight from the live spec-sheet markup
     (.specProductName, .specSku, .specIntroImage, .specSheetHeader img,
     .enhanced-content-bullets li). A small JSON config block only supplies
     the handful of fields that markup doesn't render anywhere (vendor name,
     barcode, shop domain) — see usi-specsheet-pdf-button.liquid.

   OUTPUT
     Real vector text via jsPDF — selectable, searchable, small file size,
     crisp at any zoom. Not a canvas screenshot of the page (no html2canvas).

   HARD REQUIREMENT
     The sheet must always fit on exactly one page. Unlike a resource-heavy
     catalog document, this only ever holds one product's specs, so rather
     than overflow to a second page, type shrinks step-by-step (see SCALES)
     until everything clears the footer.

   DEPENDENCY
     jsPDF 2.5.2 UMD, lazy-loaded on first click only — nothing is fetched on
     pages where nobody asks for a spec sheet. Prefers a copy in the theme's
     own assets (self-hosted, immune to a blocked third-party CDN); falls
     back to the public cdnjs build if that asset was never uploaded.

   PUBLIC API
     window.USISpecSheet.generate(root)   → Promise   (root = #dvContainer2)
   =========================================================================== */

(function () {
  'use strict';

  var JSPDF_SRC =
    'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.2/jspdf.umd.min.js';

  /* --- Brand palette -------------------------------------------------------
     USI's masthead is a gradient (purple → red → blue), not a flat brand
     color, so it's drawn as a set of gradient stops rather than a single
     fill. Confirmed live via computed style on .specSheetHeader:
     linear-gradient(100deg, #6c217f, #ee212e, #3091d3). jsPDF strips are
     painted left-to-right (0deg-equivalent), which reads the same as the
     live 100deg bar at this bar's aspect ratio. Override via cfg.gradient
     (array of {stop:0-1, rgb:[r,g,b]}) if the theme's gradient ever
     changes, so nobody has to touch this file. */
  var GRADIENT_DEFAULT = [
    { stop: 0.00, rgb: [108, 33, 127] },  // #6c217f
    { stop: 0.50, rgb: [238, 33, 46] },   // #ee212e
    { stop: 1.00, rgb: [48, 145, 211] }   // #3091d3
  ];
  var GRADIENT = GRADIENT_DEFAULT;

  var INK        = [26, 23, 24];    /* #1a1718 — .itemLabel / .titleSpec on the page */
  var INK_MID     = [58, 54, 55];   /* #3a3637 — .itemValue on the page */
  var INK_LIGHT   = [140, 148, 157];
  var RULE        = [220, 220, 220]; /* #dcdcdc — .item border-bottom on the page */
  var RULE_HEAVY  = [26, 23, 24];    /* .titleSpec border-bottom (3px, same as ink) */
  var ZEBRA       = [246, 247, 248];
  var WHITE       = [255, 255, 255];
  var GREY        = [140, 140, 140]; /* solid footer accent bar */
  var SKU_PILL_BG = [240, 240, 240]; /* #f0f0f0 — matches the live .specSku chip */

  /* --- Type scale ------------------------------------------------------- */
  var T = {
    title:      21,     /* .specProductName */
    sku:        7.5,    /* .specSku — small text inside the pill, not a full line */
    feature:    9,       /* bullet list items */
    groupHead:  10,      /* .titleSpec — section headings ("PRODUCT DIMENSIONS", "WARRANTY", etc.) */
    row:        8,       /* spec label + value text (both cells share this size) */
    footer:     7.4
  };

  /* --- Page geometry (points; 72pt = 1in) --------------------------------- */
  var PAGE_W = 612;   /* 8.5in */
  var PAGE_H = 792;   /* 11in  */
  var MARGIN = 36;    /* 0.5in */
  var FOOTER_Y = 760;
  var GRADIENT_BAR_H = 64;

  var CONTENT_W = PAGE_W - MARGIN * 2;

  /* Shrink-to-fit ladder, largest first. The one-page requirement is hard,
     so unlike a resources catalog this never overflows to a second page —
     it just keeps shrinking until the last tier, which is a best-effort
     floor for a product with an unusually long spec list. */
  var SCALES = [1, 0.94, 0.88, 0.82, 0.76, 0.7, 0.64, 0.58];

  /* ======================================================================
     Library loading
     ====================================================================== */

  var jspdfPromise = null;

  function injectScript(src) {
    return new Promise(function (resolve, reject) {
      var s = document.createElement('script');
      s.src = src;
      s.async = true;
      s.onload = function () {
        if (window.jspdf && window.jspdf.jsPDF) resolve(window.jspdf.jsPDF);
        else reject(new Error('Loaded but window.jspdf.jsPDF is missing: ' + src));
      };
      s.onerror = function () { reject(new Error('Could not load jsPDF from ' + src)); };
      document.head.appendChild(s);
    });
  }

  function loadJsPDF(localUrl) {
    if (window.jspdf && window.jspdf.jsPDF) return Promise.resolve(window.jspdf.jsPDF);
    if (jspdfPromise) return jspdfPromise;

    jspdfPromise = (localUrl ? injectScript(localUrl) : Promise.reject())
      .catch(function (err) {
        if (localUrl) console.warn('[SpecSheet] local jsPDF unavailable, trying CDN', err && err.message);
        return injectScript(JSPDF_SRC);
      })
      .catch(function (err) { jspdfPromise = null; throw err; });

    return jspdfPromise;
  }

  /* ======================================================================
     Helpers
     ====================================================================== */

  function txt(node) {
    if (!node) return '';
    return (node.textContent || '').replace(/\s+/g, ' ').trim();
  }

  function each(list, fn) { Array.prototype.forEach.call(list || [], fn); }

  function toText(v) {
    if (v == null) return '';
    if (Array.isArray(v)) return v.map(toText).filter(Boolean).join(' · ');
    if (typeof v === 'object') return toText(v.value != null ? v.value : '');
    return String(v).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
  }

  /* SKU → filename: SpecSheet_USI_[SKU].pdf, "/" and whitespace become "_". */
  function buildFileName(sku) {
    var safe = String(sku == null ? '' : sku)
      .trim()
      .replace(/[\/\\\s]+/g, '_')
      .replace(/[^A-Za-z0-9._-]/g, '')
      .replace(/_{2,}/g, '_')
      .replace(/^_+|_+$/g, '');
    return 'SpecSheet_USI_' + (safe || 'PRODUCT') + '.pdf';
  }

  function absUrl(u) {
    if (!u) return '';
    if (/^https?:\/\//i.test(u)) return u;
    return 'https://' + u.replace(/^\/+/, '');
  }

  function readConfig(root) {
    var el = root.querySelector('[data-usi-pdf-config]');
    if (!el) return {};
    try { return JSON.parse(el.textContent) || {}; }
    catch (e) { console.warn('[SpecSheet] config JSON is malformed', e); return {}; }
  }

  function parseColor(str, fallback) {
    if (!str) return fallback;
    str = str.trim();
    var m = str.match(/^#([0-9a-f]{3}|[0-9a-f]{6})$/i);
    if (m) {
      var h = m[1];
      if (h.length === 3) h = h[0] + h[0] + h[1] + h[1] + h[2] + h[2];
      return [parseInt(h.slice(0, 2), 16), parseInt(h.slice(2, 4), 16), parseInt(h.slice(4, 6), 16)];
    }
    m = str.match(/^rgba?\(\s*([\d.]+)[\s,]+([\d.]+)[\s,]+([\d.]+)/i);
    if (m) return [Math.round(+m[1]), Math.round(+m[2]), Math.round(+m[3])];
    return fallback;
  }

  function readGradient(cfg) {
    if (cfg && Array.isArray(cfg.gradient) && cfg.gradient.length >= 2) {
      var parsed = cfg.gradient.map(function (g) {
        return { stop: +g.stop, rgb: parseColor(g.color || g.rgb, [0, 0, 0]) };
      }).filter(function (g) { return !isNaN(g.stop); });
      if (parsed.length >= 2) { GRADIENT = parsed; return; }
    }
    GRADIENT = GRADIENT_DEFAULT;
  }

  function lerp(a, b, t) { return a + (b - a) * t; }

  function gradientColorAt(t) {
    t = Math.max(0, Math.min(1, t));
    for (var i = 0; i < GRADIENT.length - 1; i++) {
      var a = GRADIENT[i], b = GRADIENT[i + 1];
      if (t >= a.stop && t <= b.stop) {
        var span = b.stop - a.stop || 1;
        var lt = (t - a.stop) / span;
        return [
          Math.round(lerp(a.rgb[0], b.rgb[0], lt)),
          Math.round(lerp(a.rgb[1], b.rgb[1], lt)),
          Math.round(lerp(a.rgb[2], b.rgb[2], lt))
        ];
      }
    }
    return GRADIENT[GRADIENT.length - 1].rgb;
  }

  /* ======================================================================
     Reading the live section
     ----------------------------------------------------------------------
     Matches the ACTUAL rendered spec-sheet DOM (confirmed via live
     inspection of universalsecurity.com — the .spec-sheet-wrapper /
     .spec-header-bar / .itemLabel / .itemValue names in the pasted
     spec-sheet.liquid / specifications-table.liquid source do NOT exist on
     the live page; these are the real ones):
       #dvContainer2                        → root wrapper (no .spec-sheet-wrapper exists)
       .specSheetHeader img                 → logo
       .specProductName                     → title
       .specSku                             → "SKU: XXXX" (SKU pulled out)
       .enhanced-content-bullets li         → bullets, across TWO separate
                                               .enhanced-content-bullets blocks
                                               (main features + "Special
                                               Features:"); "View Product
                                               Details" link excluded by text
       .specIntroImage                      → hero photo (no wrapper div)
       .grid-container
         .titleSpec                         → group heading
         .item (plain, no class split)      → alternating label/value pairs,
                                               paired by POSITION (even index
                                               = label, odd index = value)
     ====================================================================== */

  function readHeader(root, cfg) {
    var logoImg = root.querySelector('.specSheetHeader img');
    var titleEl = root.querySelector('.specProductName');
    var skuEl = root.querySelector('.specSku');
    var heroImg = root.querySelector('.specIntroImage');

    var skuRaw = txt(skuEl);
    var sku = skuRaw.replace(/^(item\s*number|sku)\s*:?\s*/i, '').trim();

    var bullets = [];
    each(root.querySelectorAll('.enhanced-content-bullets li'), function (li) {
      /* The "View Product Details" link is a PDP-navigation affordance for
         the on-page view, not a spec — the PDF instead gets a live link from
         the SKU line (see drawHeader), matching how the Cree sheet links its
         SKU rather than repeating a "view details" bullet. There's no class
         on this li/link to key off of on the live page, so match by text. */
      var t = txt(li);
      if (/^view product details$/i.test(t)) return;
      if (t) bullets.push(t);
    });

    return {
      logo: logoImg ? logoImg.getAttribute('src') : (cfg.logo || ''),
      title: toText(cfg.title) || txt(titleEl) || document.title,
      sku: toText(cfg.sku) || sku,
      barcode: toText(cfg.barcode),
      url: toText(cfg.url) || (root.ownerDocument === document ? window.location.href : ''),
      domain: toText(cfg.domain),
      vendor: toText(cfg.vendor) || 'Universal Security Instruments',
      image: heroImg ? heroImg.getAttribute('src') : (cfg.image || ''),
      bullets: bullets
    };
  }

  /* Renames a handful of on-page spec labels for the PDF only — the live
     page keeps its own wording (it's generated from the metafield key), the
     printed sheet just uses a shorter label for the same row. Add more
     lowercase-key -> display-label pairs here as needed. */
  var LABEL_OVERRIDES = {
    'product dimensions': 'Dimensions'
  };
  function applyLabelOverride(k) {
    var hit = LABEL_OVERRIDES[k.toLowerCase()];
    return hit || k;
  }

  function readGroups(root) {
    var out = [];
    /* No .spec-table-container wrapper exists on the live page — the
       .grid-container divs are direct children of the root, interspersed
       with .specSpacer divs, so query the whole root rather than a wrapper. */
    each(root.querySelectorAll('.grid-container'), function (gc) {
      var titleEl = gc.querySelector('.titleSpec');
      var title = txt(titleEl);
      var rows = [];

      /* The live page has no .itemLabel/.itemValue class split — every row
         cell is a plain <div class="item">, alternating label, value, label,
         value... by DOM ORDER ONLY. Pair them by position instead. */
      var items = [];
      each(gc.children, function (child) {
        if (child === titleEl) return;
        if (child.classList.contains('item')) items.push(child);
      });

      for (var i = 0; i < items.length; i += 2) {
        var k = applyLabelOverride(txt(items[i]));
        var v = items[i + 1] ? txt(items[i + 1]) : '';
        if (k && v) rows.push({ k: k, v: v });
      }

      if (rows.length) out.push({ title: (title || 'Specifications').toUpperCase(), rows: rows });
    });
    return out;
  }

  /* ======================================================================
     Product image — same crop + navy-matte technique as the Cree generator
     ----------------------------------------------------------------------
     Product photography here sits on a white/light backdrop rather than a
     dark masthead, so the matte target is WHITE, not brand navy — the crop
     logic and edge-sampling are identical, only the fill color differs.
     ====================================================================== */

  function contentBounds(ctx, w, h) {
    var d = ctx.getImageData(0, 0, w, h).data;
    function px(x, y) { var i = (y * w + x) * 4; return [d[i], d[i + 1], d[i + 2], d[i + 3]]; }

    var edge = [];
    var estep = Math.max(1, Math.floor(Math.min(w, h) / 100));
    for (var ex = 0; ex < w; ex += estep) { edge.push(px(ex, 0)); edge.push(px(ex, h - 1)); }
    for (var ey = 0; ey < h; ey += estep) { edge.push(px(0, ey)); edge.push(px(w - 1, ey)); }

    var bg = [0, 1, 2].map(function (c) {
      return edge.reduce(function (a, p) { return a + p[c]; }, 0) / edge.length;
    });
    var bgAlpha = edge.reduce(function (a, p) { return a + p[3]; }, 0) / edge.length;

    var TOL = 46;
    var minX = w, minY = h, maxX = -1, maxY = -1;
    var step = Math.max(1, Math.floor(Math.min(w, h) / 400));

    for (var y = 0; y < h; y += step) {
      for (var x = 0; x < w; x += step) {
        var i = (y * w + x) * 4;
        var a = d[i + 3];
        var isBg = bgAlpha < 16
          ? a < 16
          : (a < 16 || (Math.abs(d[i] - bg[0]) + Math.abs(d[i + 1] - bg[1]) + Math.abs(d[i + 2] - bg[2])) < TOL);
        if (!isBg) {
          if (x < minX) minX = x;
          if (x > maxX) maxX = x;
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
        }
      }
    }

    if (maxX < 0 || maxY < 0) return null;

    var pad = Math.round(Math.min(w, h) * 0.02);
    minX = Math.max(0, minX - pad); minY = Math.max(0, minY - pad);
    maxX = Math.min(w - 1, maxX + pad); maxY = Math.min(h - 1, maxY + pad);

    var bw = maxX - minX + 1, bh = maxY - minY + 1;
    if (bw < w * 0.12 || bh < h * 0.12) return null;

    return { x: minX, y: minY, w: bw, h: bh, bg: bg, bgAlpha: bgAlpha };
  }

  function loadImage(url, matteRGB, format) {
    /* format defaults to JPEG (smaller files; fine for the matted hero
       photo, which always gets a solid backing color anyway). The logo
       must be PNG: JPEG has no alpha channel, so exporting a transparent
       logo as JPEG silently bakes in an opaque box wherever it was
       transparent — that's what caused the white square behind the logo
       on the gradient bar. */
    format = format === 'PNG' ? 'PNG' : 'JPEG';
    return new Promise(function (resolve) {
      if (!url) return resolve(null);
      if (url.indexOf('//') === 0) url = window.location.protocol + url;

      var done = false;
      var finish = function (val) { if (!done) { done = true; resolve(val); } };
      setTimeout(function () { finish(null); }, 8000);

      var img = new Image();
      img.crossOrigin = 'anonymous';
      img.decoding = 'sync';

      img.onload = function () {
        try {
          var maxW = 1000;
          var scale = Math.min(1, maxW / (img.naturalWidth || maxW));
          var w = Math.max(1, Math.round((img.naturalWidth || 1) * scale));
          var h = Math.max(1, Math.round((img.naturalHeight || 1) * scale));

          var raw = document.createElement('canvas');
          raw.width = w; raw.height = h;
          var rctx = raw.getContext('2d', { willReadFrequently: true });
          rctx.drawImage(img, 0, 0, w, h);

          var box = null;
          try { box = contentBounds(rctx, w, h); } catch (e) { box = null; }
          if (!box) box = { x: 0, y: 0, w: w, h: h, bg: null, bgAlpha: 0 };

          var out = document.createElement('canvas');
          out.width = box.w; out.height = box.h;
          var octx = out.getContext('2d');

          /* matteRGB === null means "keep native background" (used for the
             logo, which has its own transparent PNG) — skip the matte-blend
             entirely rather than let box.bg trigger a matteRGB[0] crash. */
          if (box.bg && matteRGB) {
            var raw2 = rctx.getImageData(box.x, box.y, box.w, box.h);
            var rd = raw2.data;
            var bg2 = box.bg;
            var LOW = 20, HIGH = 90;

            for (var i = 0; i < rd.length; i += 4) {
              var alphaT = rd[i + 3] / 255;
              var colorDist = Math.abs(rd[i] - bg2[0]) + Math.abs(rd[i + 1] - bg2[1]) + Math.abs(rd[i + 2] - bg2[2]);
              var colorT = Math.max(0, Math.min(1, (colorDist - LOW) / (HIGH - LOW)));
              var t = alphaT * colorT;

              rd[i]     = Math.round(matteRGB[0] * (1 - t) + rd[i]     * t);
              rd[i + 1] = Math.round(matteRGB[1] * (1 - t) + rd[i + 1] * t);
              rd[i + 2] = Math.round(matteRGB[2] * (1 - t) + rd[i + 2] * t);
              rd[i + 3] = 255;
            }
            octx.putImageData(raw2, 0, 0);
          } else if (matteRGB) {
            octx.fillStyle = 'rgb(' + matteRGB[0] + ',' + matteRGB[1] + ',' + matteRGB[2] + ')';
            octx.fillRect(0, 0, box.w, box.h);
            octx.drawImage(raw, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
          } else {
            /* No matte requested (logo) — just crop, keep native transparency. */
            octx.drawImage(raw, box.x, box.y, box.w, box.h, 0, 0, box.w, box.h);
          }

          var mime = format === 'PNG' ? 'image/png' : 'image/jpeg';
          finish({ data: out.toDataURL(mime, 0.92), w: box.w, h: box.h, format: format });
        } catch (e) {
          finish(null);
        }
      };
      img.onerror = function () { finish(null); };
      img.src = url;
    });
  }

  /* ======================================================================
     Drawing primitives
     ====================================================================== */

  function fill(doc, rgb) { doc.setFillColor(rgb[0], rgb[1], rgb[2]); }
  function stroke(doc, rgb) { doc.setDrawColor(rgb[0], rgb[1], rgb[2]); }
  function ink(doc, rgb) { doc.setTextColor(rgb[0], rgb[1], rgb[2]); }
  function font(doc, weight) { doc.setFont('helvetica', weight === 'bold' ? 'bold' : 'normal'); }

  function drawGradientBar(doc, h) {
    /* jsPDF has no native linear-gradient fill, so the bar is painted as many
       thin vertical strips, each colored from gradientColorAt() at that x
       position — visually indistinguishable from a true gradient at print
       resolution, and it keeps the header a simple flat-color-per-strip
       operation rather than an embedded raster. */
    var steps = 160;
    var stepW = PAGE_W / steps;
    for (var i = 0; i < steps; i++) {
      var t = i / (steps - 1);
      fill(doc, gradientColorAt(t));
      doc.rect(i * stepW, 0, stepW + 0.75, h, 'F');
    }
  }

  /* ======================================================================
     Header
     ====================================================================== */

  function drawHeader(doc, d, img, logo) {
    drawGradientBar(doc, GRADIENT_BAR_H);

    if (logo) {
      var maxLogoW = 190, maxLogoH = GRADIENT_BAR_H - 24;
      var lsc = Math.min(maxLogoW / logo.w, maxLogoH / logo.h);
      var lw = logo.w * lsc, lh = logo.h * lsc;
      doc.addImage(logo.data, logo.format || 'PNG', MARGIN, (GRADIENT_BAR_H - lh) / 2, lw, lh);
    }

    var y = GRADIENT_BAR_H + 34;

    /* --- Hero image, top-right, sized to a square box; text column derives
       its width from whatever room is left, matching the Cree layout's
       "size the slot to the artwork" approach. */
    var MAX_IMG_W = 230, MAX_IMG_H = 230;
    var drawW = 0, drawH = 0;
    if (img) {
      var sc = Math.min(MAX_IMG_W / img.w, MAX_IMG_H / img.h);
      drawW = img.w * sc;
      drawH = img.h * sc;
    }
    var IMG_RIGHT_PAD = 0;
    var TEXT_IMG_GAP = 28;
    var imgX = PAGE_W - MARGIN - IMG_RIGHT_PAD - drawW;
    var textW = img ? (imgX - MARGIN - TEXT_IMG_GAP) : CONTENT_W;

    /* --- Title --- */
    ink(doc, INK);
    font(doc, 'bold');
    doc.setFontSize(T.title);
    var titleLines = doc.splitTextToSize(String(d.title || 'Product'), textW).slice(0, 3);
    var titleLead = T.title * 1.14;
    titleLines.forEach(function (line, i) {
      doc.text(line, MARGIN, y + i * titleLead);
    });
    var titleBottom = y + (titleLines.length - 1) * titleLead;

    /* --- SKU, drawn as a small rounded pill (matching the live .specSku
       chip: light grey background, small text, rounded corners) and linked
       back to the product page (mirrors the removed "View Product Details"
       bullet). --- */
    var skuTop = titleBottom + 18;
    var skuBottom = titleBottom;
    if (d.sku) {
      font(doc, 'normal');
      doc.setFontSize(T.sku);
      var skuLine = 'SKU: ' + d.sku;
      var padX = 8, padY = 3.5;
      var skuTextW = doc.getTextWidth(skuLine);
      var pillH = T.sku + padY * 2;
      var pillW = skuTextW + padX * 2;

      fill(doc, SKU_PILL_BG);
      if (typeof doc.roundedRect === 'function') {
        doc.roundedRect(MARGIN, skuTop, pillW, pillH, pillH / 2, pillH / 2, 'F');
      } else {
        doc.rect(MARGIN, skuTop, pillW, pillH, 'F');
      }

      ink(doc, INK);
      var skuBaseline = skuTop + pillH / 2 + T.sku * 0.34;
      doc.text(skuLine, MARGIN + padX, skuBaseline);

      if (d.url) {
        doc.link(MARGIN, skuTop, pillW, pillH, { url: absUrl(d.url) });
      }

      skuBottom = skuTop + pillH;
    }

    var bulletsTop = skuBottom + 22;

    /* --- Bullets — capped at 3, matching the desktop short-features rows
       (short_features_1/2/3) in spec-sheet.liquid; ideal-usage / special
       -features bullets and the view-details link are intentionally not
       repeated here so the block stays a tight, scannable "Key Features"
       list rather than the full on-page list. */
    font(doc, 'normal');
    doc.setFontSize(T.feature);
    var bulletLead = T.feature * 1.32;
    var bullets = (d.bullets || []).slice(0, 3).map(function (b) {
      return doc.splitTextToSize(b, textW - 13);
    });

    var by = bulletsTop;
    bullets.forEach(function (lines) {
      fill(doc, gradientColorAt(0.15));
      doc.circle(MARGIN + 2.2, by - T.feature * 0.32, 1.6, 'F');
      ink(doc, INK_MID);
      lines.forEach(function (line, i) {
        doc.text(line, MARGIN + 13, by + i * bulletLead);
      });
      by += lines.length * bulletLead + 7;
    });

    if (img) {
      doc.addImage(img.data, img.format || 'JPEG', imgX, y - 8, drawW, drawH);
    }

    var headerBottom = Math.max(by, img ? y - 8 + drawH : 0) + 18;
    return headerBottom;
  }

  /* ======================================================================
     Spec groups — full-width stacked sections, matching the live page's
     .grid-container layout (not a two-column card grid): each group's
     .titleSpec becomes a bold uppercase heading with a heavy rule under it,
     then rows run label/value pairs two per printed row exactly as the
     15/35/15/35 CSS grid does on-page.
     ====================================================================== */

  function metrics(scale) {
    return {
      headFs: T.groupHead * scale,
      headGap: 9 * scale,
      headMarginTop: 28 * scale, /* gap above each group heading — space between groups */
      rowFs: T.row * scale,
      line: T.row * 1.05 * scale,
      rowPad: T.row * 0.95 * scale
    };
  }

  function measureGroups(doc, groups, M) {
    /* Label gets more of the row (was 15/35) so longer labels like "Battery
       chemistry type" and "Rechargeable battery" fit on one line instead of
       wrapping; the pair still sums to 0.50 so column 2's position/right
       edge doesn't shift. */
    var labelW = CONTENT_W * 0.22;
    var valueW = CONTENT_W * 0.28;

    return groups.map(function (g) {
      var pairs = [];
      for (var i = 0; i < g.rows.length; i += 2) {
        pairs.push([g.rows[i], g.rows[i + 1] || null]);
      }

      var h = M.headMarginTop + M.headFs + M.headGap + 3; /* heading + rule */

      var rows = pairs.map(function (pair) {
        doc.setFontSize(M.rowFs);
        var maxLines = 1;
        var cells = pair.map(function (cell) {
          if (!cell) return null;
          font(doc, 'bold');
          var L = doc.splitTextToSize(cell.k, labelW - 4);
          font(doc, 'normal');
          var V = doc.splitTextToSize(cell.v, valueW - 6);
          maxLines = Math.max(maxLines, L.length, V.length);
          return { L: L, V: V };
        });
        var rh = maxLines * M.line + M.rowPad;
        return { cells: cells, h: rh };
      });

      var bodyH = rows.reduce(function (a, r) { return a + r.h; }, 0);
      return { title: g.title, rows: rows, h: h + bodyH, labelW: labelW, valueW: valueW };
    });
  }

  function drawGroups(doc, measured, y, M) {
    /* Label gets more of the row (was 15/35) so longer labels like "Battery
       chemistry type" and "Rechargeable battery" fit on one line instead of
       wrapping; the pair still sums to 0.50 so column 2's position/right
       edge doesn't shift. */
    var labelW = CONTENT_W * 0.22;
    var valueW = CONTENT_W * 0.28;
    var col2X = MARGIN + labelW + valueW + 24;

    measured.forEach(function (g) {
      y += M.headMarginTop;

      ink(doc, INK);
      font(doc, 'bold');
      doc.setFontSize(M.headFs);
      doc.text(g.title, MARGIN, y + M.headFs * 0.78, { charSpace: 0.4 });

      var ruleY = y + M.headFs + M.headGap * 0.4;
      stroke(doc, RULE_HEAVY);
      doc.setLineWidth(1.6);
      doc.line(MARGIN, ruleY, PAGE_W - MARGIN, ruleY);

      y = ruleY + M.headGap;

      g.rows.forEach(function (r, ri) {
        var baseline = y + M.rowPad * 0.7 + M.rowFs * 0.8;

        [0, 1].forEach(function (ci) {
          var cell = r.cells[ci];
          if (!cell) return;
          var x = ci === 0 ? MARGIN : col2X;

          doc.setFontSize(M.rowFs);
          ink(doc, INK);
          font(doc, 'bold');
          cell.L.forEach(function (line, li) {
            doc.text(line, x, baseline + li * M.line);
          });

          ink(doc, INK_MID);
          font(doc, 'normal');
          cell.V.forEach(function (line, li) {
            doc.text(line, x + labelW + 6, baseline + li * M.line);
          });
        });

        y += r.h;

        stroke(doc, RULE);
        doc.setLineWidth(0.6);
        doc.line(MARGIN, y, PAGE_W - MARGIN, y);
      });
    });

    return y;
  }

  /* ======================================================================
     Footer
     ----------------------------------------------------------------------
     Confirmed live: this product has NO .print-footer element at all — the
     spec-sheet view flows straight into the site's global mega-footer (nav,
     newsletter, copyright), which must NOT be captured into the PDF. So
     there is no on-page footer line to read; the fallback line below
     (vendor name + domain, both from cfg since neither is guaranteed to be
     visible anywhere on the page) is what actually prints for every
     product, not a rare edge case. If a future template adds a real
     .print-footer element, this still prefers that text automatically.
     ====================================================================== */

  function drawFooter(doc, footerText, domain) {
    stroke(doc, RULE);
    doc.setLineWidth(0.6);
    doc.line(MARGIN, FOOTER_Y, PAGE_W - MARGIN, FOOTER_Y);

    /* Right-aligned, bottom-of-page — vendor name + domain sit at the right
       margin instead of centered. */
    var rightEdge = PAGE_W - MARGIN;
    ink(doc, INK_LIGHT);
    font(doc, 'normal');
    doc.setFontSize(T.footer);
    doc.text(footerText, rightEdge, FOOTER_Y + 14, { align: 'right' });

    if (domain) {
      var idx = footerText.toLowerCase().lastIndexOf(domain.toLowerCase());
      if (idx >= 0) {
        var full = doc.getTextWidth(footerText);
        var before = doc.getTextWidth(footerText.slice(0, idx));
        var seg = doc.getTextWidth(footerText.slice(idx, idx + domain.length));
        var startX = rightEdge - full + before;
        doc.link(startX, FOOTER_Y + 5, seg, 11, { url: absUrl(domain) });
      }
    }

    /* Solid grey accent bar instead of the brand gradient — a neutral,
       print-friendly footer strip. */
    fill(doc, GREY);
    doc.rect(0, PAGE_H - 6, PAGE_W, 6, 'F');
  }

  /* ======================================================================
     Compose
     ====================================================================== */

  function build(jsPDF, d, groups, img, logo, footerText) {
    var doc = new jsPDF({ unit: 'pt', format: 'letter', compress: true });

    doc.setProperties({
      title: 'USI Spec Sheet — ' + (d.title || '') + ' (' + (d.sku || '') + ')',
      subject: 'Product specification sheet',
      author: d.vendor || 'Universal Security Instruments',
      creator: 'Universal Security Instruments product page'
    });

    var headerBottom = drawHeader(doc, d, img, logo);
    var availH = FOOTER_Y - 14 - headerBottom;

    var M, measured, totalH;
    for (var i = 0; i < SCALES.length; i++) {
      M = metrics(SCALES[i]);
      measured = measureGroups(doc, groups, M);
      totalH = measured.reduce(function (a, g) { return a + g.h; }, 0);
      if (totalH <= availH) break;
      /* last tier: proceed anyway even if it slightly overflows — the hard
         one-page requirement means we never add a page 2, and by this point
         the sheet is already at the smallest readable size. */
    }

    drawGroups(doc, measured, headerBottom, M);
    drawFooter(doc, footerText, d.domain);

    return doc;
  }

  /* ======================================================================
     Entry point
     ====================================================================== */

  function generate(root) {
    if (!root) return Promise.reject(new Error('No #dvContainer2 element found'));

    var cfg = readConfig(root);
    readGradient(cfg);

    var d = readHeader(root, cfg);
    var groups = readGroups(root);

    var footerEl = root.querySelector('.print-footer');
    var footerText = txt(footerEl) || (d.domain ? d.vendor + ' | ' + d.domain : d.vendor);

    return Promise.all([
      loadJsPDF(cfg.jspdf),
      loadImage(d.image, WHITE, 'JPEG'),
      loadImage(d.logo, null, 'PNG') /* PNG keeps the logo's real transparency */
    ]).then(function (res) {
      var jsPDF = res[0], img = res[1], logo = res[2];
      var doc = build(jsPDF, d, groups, img, logo, footerText);
      doc.save(buildFileName(d.sku));
    });
  }

  /* ======================================================================
     Button wiring — delegated so it survives section re-renders on variant
     change or in the theme editor.
     ====================================================================== */

  var BUSY = 'is-generating';

  function runButton(btn, work) {
    if (btn.classList.contains(BUSY)) return Promise.resolve();

    var labelEl = btn.querySelector('[data-usi-specsheet-label]') || btn;
    var original = labelEl.textContent;

    btn.classList.add(BUSY);
    btn.setAttribute('aria-busy', 'true');
    labelEl.textContent = 'Preparing…';

    return work()
      .catch(function (err) {
        console.error('[SpecSheet]', err);
        labelEl.textContent = 'Unavailable';
      })
      .then(function () {
        setTimeout(function () {
          labelEl.textContent = original;
          btn.classList.remove(BUSY);
          btn.removeAttribute('aria-busy');
        }, 900);
      });
  }

  document.addEventListener('click', function (e) {
    if (!e.target.closest) return;
    var btn = e.target.closest('[data-usi-specsheet]');
    if (!btn) return;

    var root = btn.closest('#dvContainer2') || document.querySelector('#dvContainer2');
    if (!root) return;

    e.preventDefault();
    runButton(btn, function () { return generate(root); });
  });

  window.USISpecSheet = {
    generate: generate,
    fileName: buildFileName
  };
})();